import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Mail, Building2, Phone, FileDown } from "lucide-react";

export default function ClientAccount() {
  const { user } = useAuth();
  const { t, money, date, lang } = useLang();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
  }, []);

  const paid = orders.filter((o) => ["PAYEE", "EN COURS DE PRODUCTION", "LIVREE"].includes(o.status));

  const downloadInvoice = async (o) => {
    try {
      const res = await api.get(`/orders/${o.id}/invoice`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${lang === "en" ? "invoice" : "facture"}-${o.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("acc.invoice_error"));
    }
  };

  return (
    <div className="ch-fade-up space-y-8">
      <header>
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-neutral-400">{t("acc.eyebrow")}</p>
        <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight">{t("acc.title")}</h1>
      </header>

      <div className="grid grid-cols-1 gap-4">
        <div className="rounded-sm border border-neutral-200 p-5">
          <p className="font-display text-lg font-bold">{user?.prenom} {user?.nom}</p>
          <div className="mt-3 space-y-1.5 text-sm text-neutral-600">
            {user?.societe && <p className="flex items-center gap-2"><Building2 className="h-3.5 w-3.5 text-neutral-400" />{user.societe}</p>}
            <p className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-neutral-400" />{user?.email}</p>
            {user?.phone && <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-neutral-400" />{user.phone}</p>}
          </div>
        </div>
      </div>

      <div>
        <h2 className="mb-3 font-display text-lg font-bold">{t("acc.my_paid")}</h2>
        <div className="overflow-hidden rounded-sm border border-neutral-200">
          <div className="hidden grid-cols-12 gap-4 border-b border-neutral-200 bg-neutral-50 px-6 py-3 text-xs font-semibold uppercase tracking-wider text-neutral-400 md:grid">
            <div className="col-span-4">{t("ao.col_pack")}</div>
            <div className="col-span-2">{t("common.date")}</div>
            <div className="col-span-3">{t("common.status")}</div>
            <div className="col-span-2 text-right">{t("common.price")}</div>
            <div className="col-span-1 text-right">{t("acc.invoice")}</div>
          </div>
          {paid.map((o) => (
            <div key={o.id} data-testid={`account-order-${o.id}`} className="grid grid-cols-1 gap-2 border-b border-neutral-100 px-6 py-4 last:border-0 md:grid-cols-12 md:items-center md:gap-4">
              <div className="col-span-4 font-semibold">{o.package_name}</div>
              <div className="col-span-2 text-sm text-neutral-500">{date(o.created_at)}</div>
              <div className="col-span-3"><StatusBadge status={o.status} /></div>
              <div className="col-span-2 font-display font-bold md:text-right">{money(o.price)}</div>
              <div className="col-span-1 flex md:justify-end">
                <button data-testid={`download-invoice-${o.id}`} onClick={() => downloadInvoice(o)}
                  title={t("acc.invoice")} className="rounded-sm border border-neutral-200 p-2 transition-colors hover:border-black hover:bg-neutral-50">
                  <FileDown className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
          {paid.length === 0 && <div className="px-6 py-12 text-center text-sm text-neutral-400">{t("acc.no_paid")}</div>}
        </div>
      </div>
    </div>
  );
}
